import { type RefObject, useLayoutEffect } from "react";

import type { Transform } from "./useViewport";

import type { LayoutNode } from "./layout";

interface AnchorState {
	nodeId: string;
	screenX: number;
	screenY: number;
}

interface UseChartNavigationOptions {
	positions: Map<string, LayoutNode>;
	layoutNodes: LayoutNode[];
	offsetX: number;
	offsetY: number;
	transform: Transform;
	centerOn: (x: number, y: number, scale?: number, duration?: number) => void;
	fitView: (nodes: LayoutNode[], duration?: number) => void;
	setView: (view: Transform, duration?: number) => void;
	navTargetRef: RefObject<string | null>;
	anchorRef: RefObject<AnchorState | null>;
	fitOnNextRef: RefObject<boolean>;
}

export function useChartNavigation({
	positions,
	layoutNodes,
	offsetX,
	offsetY,
	transform,
	centerOn,
	fitView,
	setView,
	navTargetRef,
	anchorRef,
	fitOnNextRef,
}: UseChartNavigationOptions) {
	useLayoutEffect(() => {
		const navTarget = navTargetRef.current;
		const anchor = anchorRef.current;
		const wantsFit = fitOnNextRef.current;
		fitOnNextRef.current = false;

		if (navTarget) {
			navTargetRef.current = null;
			const position = positions.get(navTarget);
			if (position) {
				centerOn(position.x + position.width / 2 + offsetX, position.y + position.height / 2 + offsetY, 1.15);
			}
			return;
		}

		if (anchor) {
			anchorRef.current = null;
			const nextPosition = positions.get(anchor.nodeId);
			if (nextPosition) {
				const nextContentX = nextPosition.x + offsetX + nextPosition.width / 2;
				const nextContentY = nextPosition.y + offsetY + nextPosition.height / 2;
				setView(
					{
						x: anchor.screenX - nextContentX * transform.scale,
						y: anchor.screenY - nextContentY * transform.scale,
						scale: transform.scale,
					},
					0
				);
			}
			return;
		}

		if (wantsFit && layoutNodes.length > 0) {
			fitView(layoutNodes, 0);
		}
	}, [
		centerOn,
		fitView,
		layoutNodes,
		offsetX,
		offsetY,
		positions,
		setView,
		transform.scale,
		anchorRef,
		fitOnNextRef,
		navTargetRef,
	]);
}
